<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { api, blobUrl, NetworkError } from '@/lib/http.js'
import { offline } from '@/lib/offline.js'
import { uuid as newUuid } from '@addictivegroup/addictive-offline'
import { reportError } from '@/composables/useSave.js'
import { previewKind } from '@/lib/filePreview.js'
import { offerLanguages } from '@/lib/rowTexts.js'
import { localMaterials, keptFiles } from '@/lib/materials.js'
import { useMeta } from '@/stores/meta.js'
import { useUi } from '@/stores/ui.js'
import UiBadge from '@/components/ui/UiBadge.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiModal from '@/components/ui/UiModal.vue'
import UiSpinner from '@/components/ui/UiSpinner.vue'
import FilePreview from '@/components/record/FilePreview.vue'
import RefPicker from '@/components/form/RefPicker.vue'
import RecordLink from '@/components/record/RecordLink.vue'

/**
 * Sales materials for those who sell (D-332): brochures, data sheets, presentations, case studies,
 * pictures, films, links — in the language chosen; for a record (`for`: an offer, an opportunity)
 * those fitting it first, with why (about what it has, its competitor, its customer's industry, its
 * stage); for a competitor (`competitor`) those answering it. Each looked at here, downloaded,
 * opened, shared from the phone; chosen ones noted as sent to the customer (an activity of the
 * record), or attached to the offer. One for us only stays in; one past its review date is marked.
 *
 * Offline the same, from what the device keeps (lib/materials.js): files kept open, others are
 * "online only"; "sent" waits in the queue. A material pinned "on this device" keeps all its files.
 */
const props = defineProps({
  for: { type: String, default: '' },
  competitor: { type: String, default: '' },
  regarding: { type: Object, default: null }, // { type, uuid } — where "sent" is noted; asked for when none
  offerUuid: { type: String, default: '' },
  compact: Boolean,
})
const emit = defineEmits(['attached'])
const { t } = useI18n()
const meta = useMeta()
const ui = useUi()
const L = (v) => meta.localized(v)
const items = ref(null)
// Where the list came from: Core, or the device (offline).
const source = ref('server')
const q = ref('')
const category = ref('')
const industry = ref('')
const product = ref('')
const products = ref([])
const languages = computed(() => offerLanguages(meta))
const lang = ref(languages.value[0] || 'pl')
const chosen = ref(new Set())
const preview = ref(null)
const sending = ref(null)
const busy = ref(false)
// The files this device keeps, and this person's pins of materials (uuid → pin uuid).
const kept = ref(new Set())
const pins = ref({})
const categories = computed(() => meta.dictionary('material_category').entries || [])
const industries = computed(() => meta.dictionary('industry').entries || [])
const why = (m) => t('materials.fitsHint', { why: m.matched.map((k) => t(`materials.fits.${k}`)).join(', ') })
const onDevice = computed(() => source.value === 'device')

async function load() {
  const filters = { category: category.value, industry: industry.value, q: q.value.trim(), product: product.value, competitor: props.competitor, for: props.for }
  try {
    kept.value = await keptFiles().catch(() => new Set())
    const params = new URLSearchParams(Object.entries(filters).filter(([, v]) => v))
    items.value = (await api(`/materials?${params}`)).data
    source.value = 'server'
  } catch (e) {
    if (!(e instanceof NetworkError)) return reportError(e)
    items.value = await localMaterials(filters, meta)
    source.value = 'device'
  }
}
async function loadPins() {
  try {
    pins.value = Object.fromEntries((await api('/offline/pins')).data.filter((p) => p.type === 'sales_material').map((p) => [p.pinned_uuid, p.uuid]))
  } catch {
    // offline: the pins stay as they were
  }
}
let timer = null
watch([category, industry, product, () => props.for, () => props.competitor], load, { immediate: true })
watch(q, () => {
  clearTimeout(timer)
  timer = setTimeout(load, 300)
})
loadPins()
// The device's files changed (a pin's files came in): what is here is said again.
const off = offline.on((event) => {
  if (event === 'files') keptFiles().then((s) => (kept.value = s)).catch(() => null)
})
onBeforeUnmount(() => off?.())
if (!props.compact) {
  api('/catalog/products').then((r) => (products.value = r.data.map((p) => ({ uuid: p.uuid, name: L(p.name) })))).catch(() => {})
}
// Offline: the products the kept materials are about.
watch(items, (list) => {
  if (props.compact || !onDevice.value || products.value.length) return
  const named = new Map()
  for (const m of list || []) for (const e of m.about_items || []) if (e.type === 'product') named.set(e.uuid, e.label)
  products.value = [...named].map(([uuid, name]) => ({ uuid, name }))
})

// Its files and links in the language chosen or for any; none such — all it has (D-332).
const inLang = (list) => {
  const mine = (list || []).filter((x) => !x.language || x.language === lang.value)
  return mine.length ? mine : list || []
}
const filesOf = (m) => inLang(m.files)
const linksOf = (m) => inLang(m.links)
const here = (f) => f.cached || kept.value.has(f.uuid)
// Offline only what the device keeps opens.
const reachable = (f) => !onDevice.value || here(f)
const ICONS = { pdf: 'fa-file-pdf text-red-600', image: 'fa-file-image text-emerald-600', video: 'fa-file-video text-violet-600', office: 'fa-file-powerpoint text-orange-600' }
const iconOfFile = (f) => ICONS[previewKind(f)] || 'fa-file text-faint'
const iconOf = (m) => (filesOf(m)[0] ? iconOfFile(filesOf(m)[0]) : linksOf(m)[0] ? 'fa-link text-blue-600' : 'fa-photo-film text-faint')
const previewable = (f) => !!previewKind(f)
// All its files (in the language) are on this device.
const allHere = (m) => filesOf(m).length > 0 && filesOf(m).every(here)

/** A file's address to show or save: the device's copy when it has one, else Core's. */
async function srcOf(f) {
  return (here(f) && (await offline.cachedFile(f.url))) || blobUrl(f.url)
}
async function open(m, f) {
  try {
    preview.value = { name: f.name || m.name, file: f, kind: previewKind(f), src: await srcOf(f) }
  } catch (e) {
    reportError(e)
  }
}
async function download(f) {
  try {
    const a = document.createElement('a')
    a.href = await srcOf(f)
    a.download = f.name
    a.click()
  } catch (e) {
    reportError(e)
  }
}

// Shared from the phone (mail, messengers): its files in the language, as files — where the browser can.
const canShare = typeof navigator !== 'undefined' && typeof navigator.canShare === 'function'
async function share(m) {
  try {
    const files = []
    for (const f of filesOf(m).filter(reachable)) {
      const blob = await (await fetch(await srcOf(f))).blob()
      files.push(new File([blob], f.name, { type: f.mime || blob.type }))
    }
    const data = { title: m.name, files, ...(linksOf(m)[0] ? { text: linksOf(m).map((l) => l.url).join('\n') } : {}) }
    if (!navigator.canShare(data)) return ui.warn(t('materials.cannotShare'))
    await navigator.share(data)
  } catch (e) {
    if (e?.name !== 'AbortError') reportError(e)
  }
}

// On this device: pinned, all its files kept whatever the budget; unpinned, as the rules say.
async function togglePin(m) {
  try {
    if (pins.value[m.uuid]) {
      await api(`/offline/pins/${pins.value[m.uuid]}`, { method: 'DELETE' })
      const next = { ...pins.value }
      delete next[m.uuid]
      pins.value = next
      ui.success(t('materials.unpinned', { name: m.name }))
    } else {
      const pin = (await api('/offline/pins', { method: 'POST', body: { type: 'sales_material', uuid: m.uuid, label: m.name, include_relations: false } })).data
      pins.value = { ...pins.value, [m.uuid]: pin.uuid }
      ui.success(t('materials.pinned', { name: m.name }))
    }
    offline.refreshFiles({ force: true }).catch(() => null)
  } catch (e) {
    reportError(e)
  }
}

function toggle(m) {
  const next = new Set(chosen.value)
  if (next.has(m.uuid)) next.delete(m.uuid)
  else next.add(m.uuid)
  chosen.value = next
}
async function attach(m) {
  try {
    await api(`/offers/${props.offerUuid}/documents`, { method: 'POST', body: { material: m.uuid } })
    ui.success(t('materials.attached', { name: m.name }))
    emit('attached')
  } catch (e) {
    reportError(e)
  }
}
// Noted as sent: on the record given, else one chosen here.
function startSent() {
  sending.value = { type: props.regarding?.type || 'opportunity', uuid: props.regarding?.uuid || null, note: '' }
}
/** Noted through the queue: the activity made with its uuid here, so offline it waits and goes once. */
async function sent() {
  busy.value = true
  try {
    const id = newUuid()
    const names = (items.value || []).filter((m) => chosen.value.has(m.uuid)).map((m) => m.name)
    const subject = t('materials.sentLabel', { names: names.join(', ') })
    const result = await offline.save({
      entity: 'activity', uuid: id, method: 'POST', path: '/materials/sent', kind: 'create', label: subject,
      // What the activity is, for the device to show it till Core answers.
      body: { uuid: id, uuids: [...chosen.value], regarding_type: sending.value.type, regarding_uuid: sending.value.uuid, note: sending.value.note || null,
        type: 'email', direction: 'outbound', status: 'completed', subject },
    })
    if (!['saved', 'queued'].includes(result.status)) throw new Error(result.message || t('materials.sentFailed'))
    ui.success(result.status === 'queued' ? t('materials.sentQueued', { n: chosen.value.size }) : t('materials.sentDone', { n: chosen.value.size }))
    chosen.value = new Set()
    sending.value = null
  } catch (e) {
    reportError(e)
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="space-y-3" data-testid="materials">
    <p v-if="onDevice" class="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200" data-testid="materials-offline">
      <i class="fa-solid fa-mobile-screen" aria-hidden="true"></i> {{ $t('materials.offline') }}
    </p>
    <!-- what to look for -->
    <div class="flex flex-wrap items-center gap-2">
      <input v-model="q" type="search" class="input min-w-[12rem] flex-1" :placeholder="$t('materials.search')" :aria-label="$t('common.search')" data-testid="materials-search" />
      <select v-model="category" class="input w-auto" :aria-label="$t('materials.category')" data-testid="materials-category">
        <option value="">{{ $t('materials.anyCategory') }}</option>
        <option v-for="c in categories" :key="c.key" :value="c.key">{{ L(c.label) }}</option>
      </select>
      <select v-if="!compact" v-model="product" class="input w-auto max-w-[16rem]" :aria-label="$t('catalogue.product')" data-testid="materials-product">
        <option value="">{{ $t('materials.anyProduct') }}</option>
        <option v-for="p in products" :key="p.uuid" :value="p.uuid">{{ p.name }}</option>
      </select>
      <select v-if="!compact" v-model="industry" class="input w-auto max-w-[14rem]" :aria-label="$t('materials.anyIndustry')" data-testid="materials-industry">
        <option value="">{{ $t('materials.anyIndustry') }}</option>
        <option v-for="c in industries" :key="c.key" :value="c.key">{{ L(c.label) }}</option>
      </select>
      <select v-if="languages.length > 1" v-model="lang" class="input w-auto" :aria-label="$t('common.language')"><option v-for="l in languages" :key="l" :value="l">{{ l.toUpperCase() }}</option></select>
    </div>
    <div v-if="chosen.size" class="flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm dark:bg-brand-900/30" data-testid="materials-chosen">
      {{ $t('materials.chosen', { n: chosen.size }) }}
      <span class="flex-1"></span>
      <UiButton size="sm" icon="fa-paper-plane" data-testid="materials-sent" @click="startSent">{{ $t('materials.markSent') }}</UiButton>
      <UiButton size="sm" variant="ghost" @click="chosen = new Set()">{{ $t('common.cancel') }}</UiButton>
    </div>

    <div v-if="!items" class="py-8 text-center"><UiSpinner /></div>
    <p v-else-if="!items.length" class="py-6 text-center text-sm text-faint">{{ onDevice ? $t('materials.noneOnDevice') : $t('materials.none') }}</p>
    <ul v-else :class="compact ? 'space-y-2' : 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3'">
      <li v-for="m in items" :key="m.uuid" :class="['card flex gap-3 p-3', m.matched.length ? 'ring-1 ring-brand-300 dark:ring-brand-700' : '']" data-testid="material">
        <input v-if="!m.is_internal" type="checkbox" class="mt-1" :checked="chosen.has(m.uuid)" :aria-label="$t('materials.choose')" @change="toggle(m)" />
        <i :class="['fa-solid fa-xl mt-1', iconOf(m)]" aria-hidden="true"></i>
        <div class="min-w-0 flex-1 space-y-1">
          <div class="flex items-start gap-1">
            <RecordLink entity="sales_material" :uuid="m.uuid" class="block min-w-0 flex-1 font-medium leading-snug hover:underline">{{ m.name }}</RecordLink>
            <!-- on this device: all its files here; pinned keeps them whatever the budget -->
            <i v-if="allHere(m)" class="fa-solid fa-mobile-screen mt-0.5 text-xs text-emerald-600" :title="$t('materials.onDevice')" data-testid="material-on-device" aria-hidden="true"></i>
            <button v-if="!onDevice" type="button" :class="['p-0.5 text-xs', pins[m.uuid] ? 'text-brand-600' : 'text-faint hover:text-[var(--text)]']"
                    :title="pins[m.uuid] ? $t('materials.unpin') : $t('materials.pin')" :aria-label="pins[m.uuid] ? $t('materials.unpin') : $t('materials.pin')" :aria-pressed="!!pins[m.uuid]"
                    data-testid="material-pin" @click="togglePin(m)"><i class="fa-solid fa-thumbtack" aria-hidden="true"></i></button>
            <button v-if="canShare && filesOf(m).some(reachable)" type="button" class="p-0.5 text-xs text-faint hover:text-[var(--text)]" :title="$t('materials.share')" :aria-label="$t('materials.share')" data-testid="material-share" @click="share(m)"><i class="fa-solid fa-share-nodes" aria-hidden="true"></i></button>
          </div>
          <p class="flex flex-wrap gap-1 text-xs">
            <UiBadge v-if="m.category" tone="gray">{{ L(m.category.label) }}</UiBadge>
            <UiBadge v-if="m.is_internal" tone="red" :title="$t('materials.internalHint')">{{ $t('materials.internal') }}</UiBadge>
            <UiBadge v-if="m.to_review" tone="amber" :title="$t('materials.toReviewHint')">{{ $t('materials.toReview') }}</UiBadge>
            <UiBadge v-for="k in m.matched" :key="k" tone="brand" :title="why(m)" data-testid="material-fits">{{ $t(`materials.fits.${k}`) }}</UiBadge>
          </p>
          <p v-if="m.description" class="line-clamp-2 text-xs text-muted">{{ m.description }}</p>
          <!-- its files: each looked at here or downloaded; its links: opened -->
          <ul v-if="filesOf(m).length || linksOf(m).length" class="space-y-0.5 pt-1 text-xs">
            <li v-for="f in filesOf(m)" :key="f.uuid" :class="['flex items-center gap-1.5', reachable(f) ? '' : 'opacity-60']">
              <i :class="['fa-solid fa-fw', iconOfFile(f)]" aria-hidden="true"></i>
              <button v-if="previewable(f) && reachable(f)" type="button" class="link min-w-0 truncate text-left" data-testid="material-preview" @click="open(m, f)">{{ f.name }}</button>
              <span v-else class="min-w-0 truncate">{{ f.name }}</span>
              <span v-if="f.language" class="uppercase text-faint">{{ f.language }}</span>
              <span v-if="!reachable(f)" class="text-faint">· {{ $t('materials.onlineOnly') }}</span>
              <button v-else type="button" class="p-0.5 text-faint hover:text-[var(--text)]" :title="$t('materials.download')" :aria-label="$t('materials.download')" @click="download(f)"><i class="fa-solid fa-download" aria-hidden="true"></i></button>
            </li>
            <li v-for="(l, i) in linksOf(m)" :key="`l${i}`" class="flex items-center gap-1.5">
              <i class="fa-solid fa-fw fa-arrow-up-right-from-square text-blue-600" aria-hidden="true"></i>
              <a :href="l.url" target="_blank" rel="noopener noreferrer" class="link min-w-0 truncate">{{ l.label || l.url }}</a>
              <span v-if="onDevice" class="text-faint">· {{ $t('materials.needsNetwork') }}</span>
            </li>
          </ul>
          <UiButton v-if="offerUuid && !m.is_internal && filesOf(m).length" size="xs" variant="ghost" icon="fa-paperclip" :disabled="onDevice" :title="onDevice ? $t('materials.attachOnline') : ''" data-testid="material-attach" @click="attach(m)">{{ $t('materials.attach') }}</UiButton>
        </div>
      </li>
    </ul>

    <UiModal :open="!!preview" size="xl" :title="preview?.name || ''" @close="preview = null">
      <FilePreview v-if="preview" :file="preview.file" :kind="preview.kind" :src="preview.src" />
    </UiModal>
    <UiModal :open="!!sending" size="sm" :title="$t('materials.markSent')" @close="sending = null">
      <div v-if="sending" class="space-y-3 text-sm" data-testid="materials-sent-form">
        <p class="text-muted">{{ $t('materials.sentHint') }}</p>
        <template v-if="!regarding">
          <select v-model="sending.type" class="input" @change="sending.uuid = null"><option v-for="e in ['opportunity', 'account', 'contact', 'lead']" :key="e" :value="e">{{ $t(`materials.regarding.${e}`) }}</option></select>
          <RefPicker :key="sending.type" :entity="sending.type" :allow-create="false" :model-value="sending.uuid" @update:model-value="(v) => (sending.uuid = v)" />
        </template>
        <textarea v-model="sending.note" class="input" rows="2" :placeholder="$t('materials.sentNote')"></textarea>
      </div>
      <template #footer>
        <UiButton @click="sending = null">{{ $t('common.cancel') }}</UiButton>
        <UiButton variant="primary" icon="fa-paper-plane" :loading="busy" :disabled="!sending?.uuid" data-testid="materials-sent-save" @click="sent">{{ $t('materials.markSent') }}</UiButton>
      </template>
    </UiModal>
  </div>
</template>
